import { Country, State } from 'country-state-city';
import postalCodes from 'postal-codes-js';

/**
 * Get all countries with ISO codes and names
 * Popular countries (US, CA, MX, GB, AU, FR, DE, IT, ES, NZ) are sorted first, then alphabetical
 * @returns {Array<{value: string, label: string, flag: string}>}
 */
export function getAllCountries() {
  const allCountries = Country.getAllCountries().map(country => ({
    value: country.isoCode,
    label: country.name,
    flag: country.flag,
  }));

  // Popular wine countries to show first
  const popularCountryCodes = ['US', 'CA', 'MX', 'GB', 'AU', 'FR', 'DE', 'IT', 'ES', 'NZ'];

  const popular = allCountries.filter(c => popularCountryCodes.includes(c.value));
  const others = allCountries.filter(c => !popularCountryCodes.includes(c.value));

  // Sort popular by the order in popularCountryCodes array
  popular.sort((a, b) => {
    return popularCountryCodes.indexOf(a.value) - popularCountryCodes.indexOf(b.value);
  });

  // Sort others alphabetically
  others.sort((a, b) => a.label.localeCompare(b.label));

  return [...popular, ...others];
}

/**
 * Get states/provinces for a specific country
 * @param {string} countryCode - ISO country code (e.g., 'US', 'CA')
 * @returns {Array<{value: string, label: string}>}
 */
export function getStatesByCountry(countryCode) {
  if (!countryCode) return [];

  const states = State.getStatesOfCountry(countryCode);
  return states.map(state => ({
    value: state.isoCode,
    label: state.name,
  })).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Check if a country has states/provinces
 * @param {string} countryCode - ISO country code
 * @returns {boolean}
 */
export function hasStates(countryCode) {
  if (!countryCode) return false;
  const states = State.getStatesOfCountry(countryCode);
  return states && states.length > 0;
}

/**
 * Validate postal code format for a specific country
 * @param {string} countryCode - ISO country code
 * @param {string} postalCode - Postal code to validate
 * @returns {{valid: boolean, message?: string}}
 */
export function validatePostalCode(countryCode, postalCode) {
  if (!postalCode || postalCode.trim() === '') {
    return { valid: true }; // Optional field
  }

  if (!countryCode) {
    return { valid: true }; // Can't validate without country
  }

  try {
    const result = postalCodes.validate(countryCode, postalCode);
    if (result === true) {
      return { valid: true };
    } else {
      // Get expected format for error message
      const format = getPostalCodeFormat(countryCode);
      return {
        valid: false,
        message: format ? `Invalid format. Expected: ${format}` : 'Invalid postal code format',
      };
    }
  } catch (error) {
    // If validation library doesn't support this country, accept any format
    return { valid: true };
  }
}

/**
 * Get postal code format example for a country
 * @param {string} countryCode - ISO country code
 * @returns {string|null}
 */
function getPostalCodeFormat(countryCode) {
  const formats = {
    US: '12345 or 12345-6789',
    CA: 'A1A 1A1',
    GB: 'AA9A 9AA',
    AU: '1234',
    FR: '12345',
    DE: '12345',
    IT: '12345',
    ES: '12345',
    MX: '12345',
    NZ: '1234',
  };
  return formats[countryCode] || null;
}

/**
 * Get the label for state/province field based on country
 * @param {string} countryCode - ISO country code
 * @returns {string}
 */
export function getStateLabelByCountry(countryCode) {
  const labels = {
    US: 'State',
    CA: 'Province',
    AU: 'State',
    MX: 'State',
    GB: 'County',
    IE: 'County',
    NZ: 'Region',
    IN: 'State',
    BR: 'State',
  };
  return labels[countryCode] || 'State/Province';
}

/**
 * Get the label for postal code field based on country
 * @param {string} countryCode - ISO country code
 * @returns {string}
 */
export function getPostalCodeLabelByCountry(countryCode) {
  const labels = {
    US: 'ZIP Code',
    GB: 'Postcode',
    CA: 'Postal Code',
    AU: 'Postcode',
    IE: 'Eircode',
    IN: 'PIN Code',
  };
  return labels[countryCode] || 'Postal Code';
}

/**
 * Format address for display with country-appropriate formatting
 * @param {Object} address - Address object with country, city, state, zip, etc.
 * @returns {string} Formatted address string
 */
export function formatAddressDisplay(address) {
  if (!address) return '';

  const parts = [];

  // City is always shown
  if (address.city) {
    parts.push(address.city);
  }

  // State/Province shown if available
  if (address.state) {
    parts.push(address.state);
  }

  // Postal code shown if available
  if (address.zip) {
    parts.push(address.zip);
  }

  let formatted = parts.join(', ');

  // Add country if not US (assumed default)
  if (address.country && address.country !== 'United States' && address.country !== 'US') {
    if (formatted) {
      formatted += `, ${address.country}`;
    } else {
      formatted = address.country;
    }
  }

  return formatted;
}

/**
 * Get full country name from ISO code
 * @param {string} countryCode - ISO country code
 * @returns {string} Country name
 */
export function getCountryName(countryCode) {
  if (!countryCode) return '';
  const country = Country.getCountryByCode(countryCode);
  return country ? country.name : countryCode;
}

/**
 * Get state/province name from ISO code
 * @param {string} countryCode - ISO country code
 * @param {string} stateCode - ISO state code
 * @returns {string} State name
 */
export function getStateName(countryCode, stateCode) {
  if (!countryCode || !stateCode) return stateCode || '';
  const states = State.getStatesOfCountry(countryCode);
  const state = states.find(s => s.isoCode === stateCode);
  return state ? state.name : stateCode;
}
